import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { connectorService } from "@/lib/api/services/connector.service";
import { connectorKeys } from "../keys/connectors.keys";
import type { ConnectorFilters, MyConnectorFilters } from "@/types/connector.types";

/** Fetch connector list with optional filters */
export function useConnectors(filters?: ConnectorFilters) {
  return useQuery({
    queryKey: connectorKeys.list(filters),
    queryFn: () => connectorService.list(filters),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

/** Fetch a single connector by ID (includes decrypted config) */
export function useConnector(id: string) {
  return useQuery({
    queryKey: connectorKeys.detail(id),
    queryFn: () => connectorService.getById(id),
    enabled: !!id,
  });
}

/** Fetch databases for a connector */
export function useConnectorDatabases(connectorId: string) {
  return useQuery({
    queryKey: connectorKeys.databases(connectorId),
    queryFn: () => connectorService.getDatabases(connectorId),
    enabled: !!connectorId,
  });
}

/** Fetch schemas for a connector */
export function useConnectorSchemas(connectorId: string, database?: string) {
  return useQuery({
    queryKey: connectorKeys.schemas(connectorId, database),
    queryFn: () => connectorService.getSchemas(connectorId, database),
    enabled: !!connectorId,
  });
}

/** Fetch tables for a connector */
export function useConnectorTables(
  connectorId: string,
  database?: string,
  schema?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: connectorKeys.tables(connectorId, database, schema),
    queryFn: () => connectorService.getTables(connectorId, database, schema),
    enabled: enabled && !!connectorId,
  });
}

/** Fetch columns for a specific table (lazy loaded on expand) */
export function useTableColumns(
  connectorId: string,
  tableName: string,
  database?: string,
  schema?: string,
  enabled: boolean = false,
) {
  return useQuery({
    queryKey: connectorKeys.tableColumns(connectorId, tableName, database, schema),
    queryFn: () => connectorService.getTableColumns(connectorId, tableName, database, schema),
    enabled: enabled && !!connectorId && !!tableName,
    staleTime: 5 * 60 * 1000,
  });
}

/** Preview table data with server-side pagination and sorting */
export function useTableData(
  connectorId: string,
  tableName: string,
  opts: {
    database?: string;
    schema?: string;
    page?: number;
    sort?: string | null;
    sortDir?: string;
    enabled?: boolean;
  } = {},
) {
  const { database, schema, page = 1, sort, sortDir, enabled = false } = opts;
  const PAGE_SIZE = 50;
  const offset = (page - 1) * PAGE_SIZE;

  return useQuery({
    queryKey: connectorKeys.tableData(
      connectorId,
      tableName,
      database,
      schema,
      page,
      sort,
      sortDir,
    ),
    queryFn: () =>
      connectorService.getTableData(connectorId, tableName, {
        database,
        schema,
        limit: PAGE_SIZE,
        offset,
        sort: sort || undefined,
        sortDir: sortDir || undefined,
      }),
    enabled: enabled && !!connectorId && !!tableName,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Fetch Google Sheet tabs for a selected spreadsheet */
export function useGoogleSheetTabs(
  connectorId: string,
  spreadsheetId?: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: connectorKeys.sheetTabs(connectorId, spreadsheetId),
    queryFn: () => connectorService.getGoogleSheetTabs(connectorId, spreadsheetId || ""),
    enabled: enabled && !!connectorId && !!spreadsheetId,
  });
}

/** Preview Google Sheet values for a selected tab/range */
export function useGoogleSheetValues(
  connectorId: string,
  spreadsheetId?: string,
  opts: { sheet?: string; range?: string; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: connectorKeys.sheetValues(connectorId, spreadsheetId, opts.sheet, opts.range),
    queryFn: () =>
      connectorService.getGoogleSheetValues(connectorId, spreadsheetId || "", {
        sheet: opts.sheet,
        range: opts.range,
      }),
    enabled: Boolean(opts.enabled && connectorId && spreadsheetId && opts.sheet),
  });
}

export function useRedisKeys(
  connectorId: string,
  opts: { pattern?: string; cursor?: string; count?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: connectorKeys.redisKeys(connectorId, opts.pattern, opts.cursor, opts.count),
    queryFn: () =>
      connectorService.scanRedisKeys(connectorId, {
        pattern: opts.pattern,
        cursor: opts.cursor,
        count: opts.count,
      }),
    enabled: Boolean(opts.enabled && connectorId),
  });
}

export function useRedisValue(
  connectorId: string,
  opts: { key?: string; byteLimit?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: connectorKeys.redisValue(connectorId, opts.key, opts.byteLimit),
    queryFn: () =>
      connectorService.previewRedisValue(connectorId, {
        key: opts.key || "",
        byteLimit: opts.byteLimit,
      }),
    enabled: Boolean(opts.enabled && connectorId && opts.key),
  });
}

/** Fetch connector counts by subtype */
export function useConnectorCounts(enabled: boolean = true) {
  return useQuery({
    queryKey: connectorKeys.counts(),
    queryFn: () => connectorService.getCounts(),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/** Fetch connector subtypes */
export function useConnectorSubtypes(filter?: { type?: string }) {
  return useQuery({
    queryKey: connectorKeys.subtypes(),
    queryFn: () => connectorService.listSubtypes(filter),
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch shares for a connector */
export function useConnectorShares(connectorId: string) {
  return useQuery({
    queryKey: connectorKeys.shares(connectorId),
    queryFn: () => connectorService.listShares(connectorId, { limit: 100 }),
    enabled: !!connectorId,
  });
}

/** Fetch table permissions for a share */
export function useShareTablePermissions(shareId: string) {
  return useQuery({
    queryKey: connectorKeys.tablePermissions(shareId),
    queryFn: () => connectorService.listTablePermissions(shareId),
    enabled: !!shareId,
  });
}

/** Fetch my connectors (permission-resolved) */
export function useMyConnectors(filters?: MyConnectorFilters) {
  return useQuery({
    queryKey: connectorKeys.myConnectors(filters),
    queryFn: () => connectorService.getMyConnectors(filters),
    staleTime: 10 * 60 * 1000,
  });
}
